import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { videoDimensionsSelector } from 'redux/modules/embed/selectors';
import { clamp } from 'utils/numbers';
import useEditSceneModal from '../useEditSceneModal';
import { getAnchorFromEvent, getSlotScaleInfo, zoomCrop } from '../utils';
import { useEditSceneModalStateContext } from './EditSceneModalStateContext';
import useSlots from './useSlots';

type UseSlotZoomProps = {
  slotIndex?: number;
};

export default function useSlotZoom(props?: UseSlotZoomProps) {
  const { videoId } = useEditSceneModal();

  const videoDimensions = useSelector(videoDimensionsSelector(videoId));

  const {
    dispatch,
    state: {
      controlPanelMode,
      selectedSlotIndex,
      draftCrop,
      previewDimensions,
    },
  } = useEditSceneModalStateContext();

  const { slots } = useSlots();

  const { slotIndex = selectedSlotIndex } = props ?? {};

  const slot = slots?.[slotIndex];

  const scaleInfo = getSlotScaleInfo({
    slot,
    crop: draftCrop,
    previewDimensions,
    videoDimensions,
    maxScale: 4,
  });

  const showSlider =
    controlPanelMode === 'adjust-crop' &&
    !!slot &&
    !!draftCrop &&
    !!previewDimensions &&
    !!videoDimensions &&
    !!scaleInfo;

  const { anchor, currentScale, maxScale, minScale, slotRect } =
    scaleInfo ?? {};

  const handleZoomChange = useCallback(
    (nextScale: number, anchorOverride?: { x: number; y: number }) => {
      const targetScale = clamp(nextScale, minScale, maxScale);
      const anchorToUse = anchorOverride ?? anchor;
      const nextCrop = zoomCrop(
        draftCrop,
        { scale: targetScale / currentScale, anchor: anchorToUse },
        videoDimensions,
        {
          minWidth: slotRect.width / maxScale,
          minHeight: slotRect.height / maxScale,
        },
      );

      dispatch({
        type: 'adjust-crop-update',
        payload: { crop: nextCrop },
      });
    },
    [
      anchor,
      currentScale,
      dispatch,
      draftCrop,
      maxScale,
      minScale,
      slotRect?.height,
      slotRect?.width,
      videoDimensions,
    ],
  );

  const onWheel = useCallback(
    (event: React.WheelEvent<HTMLDivElement>) => {
      if (!showSlider) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const delta = -event.deltaY;

      if (delta < 0 && currentScale <= minScale + 0.0001) {
        return;
      }

      const zoomStep = 0.0015;
      const nextScale = clamp(
        currentScale * Math.exp(delta * zoomStep),
        minScale,
        maxScale,
      );
      const nextAnchor = getAnchorFromEvent(event, slotRect, draftCrop);

      handleZoomChange(nextScale, nextAnchor);
    },
    [
      currentScale,
      draftCrop,
      handleZoomChange,
      maxScale,
      minScale,
      showSlider,
      slotRect,
    ],
  );

  return {
    handleZoomChange,
    showSlider,
    zoomProps: showSlider ? { onWheel } : undefined,
    ...scaleInfo,
  };
}
